'use client';

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';

import { ArrowLeft, ArrowRight, MapPin, Navigation, Sparkles, Sun } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@kit/ui/dialog';
import { cn } from '@kit/ui/utils';

import type { AboutBlock, CtaBlock, HeroBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import { landingImageUrl, pickLocalized, type LandingRenderContext } from './landing-context';
import styles from './atmospheric-blocks.module.css';

export { Parallax as AtmosphericParallax };

function Parallax({ children, speed = 0.12, scale = 1, axis = 'y', pointer = false, className }: { children: ReactNode; speed?: number; scale?: number; axis?: 'x' | 'y'; pointer?: boolean; className?: string }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    const view = outer?.ownerDocument.defaultView;
    if (!outer || !inner || !view?.IntersectionObserver || !view.matchMedia) return;

    const motion = view.matchMedia('(prefers-reduced-motion: reduce)');
    const mouse = view.matchMedia('(pointer: fine)');
    let visible = false;
    let frame = 0;
    let x = 0;
    let y = 0;
    const reset = () => { inner.style.transform = `scale(${scale})`; };
    const update = () => {
      frame = 0;
      if (!visible || motion.matches) return;
      const rect = outer.getBoundingClientRect();
      const offset = pointer ? Math.max(0, -rect.top) * speed : -(rect.top + rect.height / 2 - view.innerHeight / 2) * speed;
      inner.style.transform = `translate3d(${axis === 'x' ? offset : x}px, ${axis === 'y' ? offset + y : 0}px, 0) scale(${scale})`;
    };
    const schedule = () => {
      if (visible && !motion.matches && !frame) frame = view.requestAnimationFrame(update);
    };
    const onPointer = (event: PointerEvent) => {
      if (!visible || motion.matches || !mouse.matches) return;
      x = (event.clientX / view.innerWidth - 0.5) * 24;
      y = (event.clientY / view.innerHeight - 0.5) * 24;
      schedule();
    };
    const onMotion = () => {
      if (frame) view.cancelAnimationFrame(frame);
      frame = 0;
      inner.style.willChange = visible && !motion.matches ? 'transform' : 'auto';
      reset();
      schedule();
    };
    const observer = new view.IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      inner.style.willChange = visible && !motion.matches ? 'transform' : 'auto';
      if (!visible && frame) {
        view.cancelAnimationFrame(frame);
        frame = 0;
      }
      schedule();
    }, { rootMargin: '100px' });
    observer.observe(outer);
    view.addEventListener('scroll', schedule, { passive: true });
    view.addEventListener('resize', schedule);
    if (pointer) view.addEventListener('pointermove', onPointer, { passive: true });
    motion.addEventListener('change', onMotion);
    return () => {
      observer.disconnect();
      if (frame) view.cancelAnimationFrame(frame);
      view.removeEventListener('scroll', schedule);
      view.removeEventListener('resize', schedule);
      view.removeEventListener('pointermove', onPointer);
      motion.removeEventListener('change', onMotion);
    };
  }, [axis, pointer, scale, speed]);

  return <div ref={outerRef} className={className}><div ref={innerRef} className={styles.parallaxInner} style={{ transform: `scale(${scale})` }}>{children}</div></div>;
}

function AtmosphericLink({ href, children, location = false }: { href: string; children: ReactNode; location?: boolean }) {
  const className = cn(styles.button, location && styles.location);
  const content = location ? <><MapPin size={28} aria-hidden /><span className="sr-only">{children}</span></> : children;
  return /^https?:\/\//i.test(href)
    ? <a href={href} className={className} target="_blank" rel="noopener noreferrer">{content}</a>
    : <Link href={href} className={className}>{content}</Link>;
}

function Watermark({ text, reverse = false }: { text?: string; reverse?: boolean }) {
  if (!text) return null;
  return <Parallax axis="x" speed={reverse ? -0.6 : 0.6} className={styles.watermark}><span aria-hidden="true">{text}</span></Parallax>;
}

function BrandedText({ text, wordmark }: { text?: string; wordmark?: string }) {
  if (!text || !wordmark?.trim()) return text;
  const escaped = wordmark.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(?<![\\p{L}\\p{N}_])(${escaped})(?![\\p{L}\\p{N}_])`, 'giu'));
  return parts.map((part, index) => index % 2 ? <span key={index} className={styles.inlineWordmark}>{wordmark}</span> : part);
}

function StudioLocation({ location, label, ctx }: { location: NonNullable<HeroBlock['location']>; label: string; ctx: LandingRenderContext }) {
  const local = (en?: string, ar?: string) => pickLocalized(en, ar, ctx.locale);
  const track = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{ open: boolean; active: number; theme: CSSProperties; portal?: HTMLElement }>({ open: false, active: 0, theme: {} });
  const arabic = ctx.locale.startsWith('ar');
  const moveTo = (index: number) => {
    const element = track.current;
    const view = element?.ownerDocument.defaultView;
    if (!element || !view || !location.images.length) return;
    const next = (index + location.images.length) % location.images.length;
    const image = element.children[next];
    if (!image) return;
    element.scrollBy({ left: image.getBoundingClientRect().left - element.getBoundingClientRect().left, behavior: view.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    setState((current) => ({ ...current, active: next }));
  };
  return <Dialog open={state.open} onOpenChange={(open) => setState((current) => ({ ...current, open }))}>
    <DialogTrigger asChild>
      <Button type="button" variant="link" className={cn(styles.button, styles.location)} aria-label={label} onClick={(event) => {
        const trigger = event.currentTarget;
        const view = trigger.ownerDocument.defaultView;
        if (!view) return;
        const computed = view.getComputedStyle(trigger);
        const theme = Object.fromEntries(['--landing-canvas', '--landing-ink', '--primary', '--primary-foreground', '--landing-decoration'].map((name) => [name, computed.getPropertyValue(name)])) as CSSProperties;
        setState((current) => ({ ...current, active: 0, theme, portal: trigger.ownerDocument.body }));
      }}><MapPin size={28} aria-hidden /></Button>
    </DialogTrigger>
    <DialogContent portalContainer={state.portal} className={styles.locationDialog} style={state.theme} dir={arabic ? 'rtl' : 'ltr'}>
      {!!location.images.length && <div className={styles.locationCarousel} role="region" aria-roledescription={arabic ? 'عرض شرائح' : 'carousel'} aria-label={local(location.title, location.title_ar)}>
        <div ref={track} className={styles.locationTrack} onScroll={() => {
          const element = track.current;
          if (!element) return;
          const left = element.getBoundingClientRect().left;
          const nearest = Array.from(element.children).reduce((best, image, index, images) => Math.abs(image.getBoundingClientRect().left - left) < Math.abs(images[best]!.getBoundingClientRect().left - left) ? index : best, 0);
          setState((current) => current.active === nearest ? current : { ...current, active: nearest });
        }}>
          {location.images.map((image, index) => <Image key={index} src={landingImageUrl(ctx, image.imageKey)} alt={local(image.alt, image.alt_ar) ?? ''} width={1200} height={675} sizes="(min-width: 768px) 768px, 100vw" className={styles.locationPhoto} />)}
        </div>
        {location.images.length > 1 && <>
          <Button type="button" variant="link" size="icon" className={cn(styles.photoArrow, styles.photoPrevious)} aria-label={arabic ? 'الصورة السابقة' : 'Previous photo'} onClick={() => moveTo(state.active - 1)}><ArrowLeft size={20} aria-hidden /></Button>
          <Button type="button" variant="link" size="icon" className={cn(styles.photoArrow, styles.photoNext)} aria-label={arabic ? 'الصورة التالية' : 'Next photo'} onClick={() => moveTo(state.active + 1)}><ArrowRight size={20} aria-hidden /></Button>
          <div className={styles.photoDots}>{location.images.map((image, index) => <button key={image.imageKey} type="button" aria-label={arabic ? `عرض الصورة ${index + 1}` : `Show photo ${index + 1}`} aria-current={state.active === index} onClick={() => moveTo(index)}><span /></button>)}</div>
        </>}
      </div>}
      <div className={styles.locationCopy}>
        <p className={styles.eyebrow}>{local(location.eyebrow, location.eyebrow_ar)}</p>
        <DialogTitle className={styles.locationTitle}>{local(location.title, location.title_ar)}</DialogTitle>
        <DialogDescription className={styles.locationDescription}>{local(location.body, location.body_ar)}</DialogDescription>
        <div className={styles.directions}><AtmosphericLink href={location.ctaHref}><Navigation size={16} aria-hidden />{local(location.ctaLabel, location.ctaLabel_ar)}</AtmosphericLink></div>
      </div>
    </DialogContent>
  </Dialog>;
}

export function AtmosphericHero({ block, ctx }: { block: HeroBlock; ctx: LandingRenderContext }) {
  const slide = block.slides[0];
  if (!slide) return null;
  const local = (en?: string, ar?: string) => pickLocalized(en, ar, ctx.locale);
  const image = slide.imageKey || slide.imageKeyMobile;
  const mobile = slide.imageKeyMobile && slide.imageKeyMobile !== image;
  const scrollCue = block.scrollCue;
  return <section className={cn(styles.root, styles.hero)} data-atmospheric-hero="monument" data-full-bleed data-image-background>
    <Parallax speed={0.25} scale={1.12} pointer className={styles.heroBackdrop}>
      {image && <Image src={landingImageUrl(ctx, image)} alt="" fill priority sizes="100vw" className={cn(styles.cover, mobile && styles.desktopImage)} style={{ objectPosition: slide.focalPoint }} />}
      {mobile && slide.imageKeyMobile && <Image src={landingImageUrl(ctx, slide.imageKeyMobile)} alt="" fill priority sizes="100vw" className={cn(styles.cover, styles.mobileImage)} style={{ objectPosition: slide.focalPoint }} />}
      <div className={styles.heroShade} />
    </Parallax>
    <div className={styles.heroFade} aria-hidden="true" />
    <div className={styles.heroContent}>
      {slide.eyebrow && <p className={styles.heroEyebrow}>{local(slide.eyebrow, slide.eyebrow_ar)}</p>}
      <h1 className={styles.lockup}><span className={styles.wordmark}>{local(slide.headline, slide.headline_ar)}</span>{slide.emphasis && <span className={styles.brandLine}>{local(slide.emphasis, slide.emphasis_ar)}</span>}</h1>
      {slide.subheadline && <p className={styles.heroDescription}>{local(slide.subheadline, slide.subheadline_ar)}</p>}
      {slide.ctaLabel && (block.location || slide.ctaHref) && <div className={styles.heroAction}>{block.location ? <StudioLocation location={block.location} label={local(slide.ctaLabel, slide.ctaLabel_ar) ?? ''} ctx={ctx} /> : slide.ctaHref && <AtmosphericLink href={slide.ctaHref} location>{local(slide.ctaLabel, slide.ctaLabel_ar)}</AtmosphericLink>}</div>}
    </div>
    {scrollCue?.href && scrollCue.label ? <Link href={scrollCue.href} className={styles.scrollCue} onClick={(event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const document = event.currentTarget.ownerDocument;
      const view = document.defaultView;
      if (!view) return;
      const href = scrollCue.href;
      if (!href.startsWith('#') && !href.startsWith('/#')) return;
      const target = document.getElementById(href.slice(href.indexOf('#') + 1));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: view.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    }}><span>{local(scrollCue.label, scrollCue.label_ar)}</span><i aria-hidden="true" /></Link> : <div className={styles.scrollCue} aria-hidden="true"><span>{ctx.locale.startsWith('ar') ? 'مرّر' : 'Scroll'}</span><i /></div>}
  </section>;
}

export function AtmosphericAbout({ block, ctx }: { block: AboutBlock; ctx: LandingRenderContext }) {
  const local = (en?: string, ar?: string) => pickLocalized(en, ar, ctx.locale);
  const variant = block.variant ?? 'origins';
  const title = local(block.title, block.title_ar);
  const body = local(block.body, block.body_ar);
  return <section className={cn(styles.root, styles.about)} data-atmospheric-about={variant} data-full-bleed>
    <Watermark text={block.design?.watermark} reverse={variant === 'benefits'} />
    <div className={styles.aboutGrid} data-has-image={Boolean(block.imageKey)}>
      {block.imageKey && <Parallax speed={0.12} className={styles.media}>
        <figure>
          <div className={styles.imageFrame}>
            <Parallax speed={variant === 'instructor' ? 0 : 0.18} scale={variant === 'instructor' ? 1 : 1.3}>
              <Image src={landingImageUrl(ctx, block.imageKey)} alt={title ?? ''} width={1200} height={variant === 'instructor' ? 1279 : variant === 'origins' ? 1600 : 1200} sizes="(min-width: 768px) 50vw, 100vw" className={styles.aboutImage} style={{ objectPosition: block.imagePosition }} />
            </Parallax>
          </div>
          {(block.caption || block.imageBadge) && <figcaption className={styles.caption}>{block.imageBadge && <><strong>{local(block.imageBadge.title, block.imageBadge.title_ar)}</strong>{block.imageBadge.detail && <span>{local(block.imageBadge.detail, block.imageBadge.detail_ar)}</span>}</>}{local(block.caption, block.caption_ar)}</figcaption>}
        </figure>
      </Parallax>}
      <Parallax speed={-0.12} className={styles.copy}>
        {block.eyebrow && <p className={styles.eyebrow}>{local(block.eyebrow, block.eyebrow_ar)}</p>}
        <h2 className={styles.heading}>{title}{block.role && <span className={styles.role}>{local(block.role, block.role_ar)}</span>}</h2>
        {!!block.stats?.length && <dl className={styles.credentials}>{block.stats.map((stat, index) => <div key={index}><dt>{local(stat.label, stat.label_ar)}</dt><dd>{local(stat.value, stat.value_ar)}</dd></div>)}</dl>}
        {body && <div className={styles.body}>{body.split(/\n\s*\n/).map((paragraph, index) => <p key={index}><BrandedText text={paragraph} wordmark={ctx.wordmark} /></p>)}</div>}
        {!!block.features?.length && <div className={styles.features}>{block.features.map((feature, index) => <div key={index}><span className={styles.number}>{String(index + 1).padStart(2, '0')}</span><h3>{local(feature.title, feature.title_ar)}</h3><p>{local(feature.body, feature.body_ar)}</p></div>)}</div>}
        {block.note && <p className={styles.body}>{local(block.note, block.note_ar)}</p>}
        {block.callout && <aside className={styles.callout}>{block.callout.eyebrow && <p className={styles.eyebrow}>{local(block.callout.eyebrow, block.callout.eyebrow_ar)}</p>}<p>{local(block.callout.body, block.callout.body_ar)}</p></aside>}
        {!!block.tags?.length && <ul className={styles.tags}>{block.tags.map((tag, index) => <li key={index}>{local(tag, block.tags_ar?.[index])}</li>)}</ul>}
        {(block.ctaHref && block.ctaLabel) && <div className={styles.aboutAction}><AtmosphericLink href={block.ctaHref}>{local(block.ctaLabel, block.ctaLabel_ar)}</AtmosphericLink></div>}
      </Parallax>
    </div>
  </section>;
}

export function AtmosphericCta({ block, ctx }: { block: CtaBlock; ctx: LandingRenderContext }) {
  const local = (en?: string, ar?: string) => pickLocalized(en, ar, ctx.locale);
  if (block.variant === 'banner') return <section className={cn(styles.root, styles.cta, styles.banner)} data-atmospheric-cta="banner" data-full-bleed data-image-background>
    <div className={styles.bannerContent}>
      {block.eyebrow && <p className={styles.bannerEyebrow}><Sparkles size={13} aria-hidden />{local(block.eyebrow, block.eyebrow_ar)}</p>}
      <h2 className={styles.heading}><BrandedText text={local(block.headline, block.headline_ar)} wordmark={ctx.wordmark} /></h2>
      {block.body && <p className={styles.bannerBody}>{local(block.body, block.body_ar)}</p>}
      <AtmosphericLink href={block.ctaHref}>{local(block.ctaLabel, block.ctaLabel_ar)}</AtmosphericLink>
    </div>
  </section>;
  return <section className={cn(styles.root, styles.cta)} data-atmospheric-cta="atmospheric" data-full-bleed data-image-background>
    {block.imageKey && <Parallax speed={0.25} scale={1.3} className={styles.ctaBackdrop}><Image src={landingImageUrl(ctx, block.imageKey)} alt="" fill sizes="100vw" className={styles.cover} /></Parallax>}
    <div className={styles.ctaShade} />
    <Watermark text={block.design?.watermark} />
    <div className={styles.ctaContent}>
      {block.eyebrow && <p className={styles.ctaEyebrow}>{local(block.eyebrow, block.eyebrow_ar)}</p>}
      <h2 className={styles.heading}><BrandedText text={local(block.headline, block.headline_ar)} wordmark={ctx.wordmark} /></h2>
      {block.body && <p className={styles.ctaBody}>{local(block.body, block.body_ar)}</p>}
      <div className={styles.ctaActions}>
        <div className={styles.sun} aria-hidden="true"><Sun size={40} /></div>
        <AtmosphericLink href={block.ctaHref}>{local(block.ctaLabel, block.ctaLabel_ar)}</AtmosphericLink>
        {block.secondaryCtaHref && block.secondaryCtaLabel && <AtmosphericLink href={block.secondaryCtaHref}>{local(block.secondaryCtaLabel, block.secondaryCtaLabel_ar)}</AtmosphericLink>}
      </div>
    </div>
  </section>;
}
